import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import { apiGet } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

const NAVY = '#052F5D';
const PAGE_SIZE = 25;

// Mirrors ACTION_GROUPS in server/controllers/admin/auditLog.js
const ACTION_OPTIONS = [
    { value: '', label: 'All actions' },
    { value: 'view', label: 'Viewed' },
    { value: 'create', label: 'Created' },
    { value: 'update', label: 'Updated' },
    { value: 'delete', label: 'Deleted' },
    { value: 'export', label: 'Exported / Reports' },
    { value: 'amend', label: 'Amended reports' },
];

const EVENT_DISPLAY = {
    PHI_ACCESS: { label: 'Viewed', icon: <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(5,47,93,0.08)', fg: NAVY, border: 'rgba(5,47,93,0.15)' },
    PHI_CREATE: { label: 'Created', icon: <AddCircleOutlineRoundedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(46,125,50,0.10)', fg: '#1B5E20', border: 'rgba(46,125,50,0.25)' },
    PHI_UPDATE: { label: 'Updated', icon: <EditOutlinedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(237,108,2,0.10)', fg: '#9A5800', border: 'rgba(237,108,2,0.25)' },
    PHI_DELETE: { label: 'Deleted', icon: <DeleteOutlineRoundedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(198,40,40,0.08)', fg: '#9B2C2C', border: 'rgba(198,40,40,0.25)' },
    PHI_EXPORT: { label: 'Exported', icon: <IosShareRoundedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(2,119,189,0.08)', fg: '#01579B', border: 'rgba(2,119,189,0.25)' },
    REPORT_GENERATED: { label: 'Report generated', icon: <IosShareRoundedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(2,119,189,0.08)', fg: '#01579B', border: 'rgba(2,119,189,0.25)' },
    REPORT_VIEWED: { label: 'Report viewed', icon: <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(5,47,93,0.08)', fg: NAVY, border: 'rgba(5,47,93,0.15)' },
    REPORT_EMAILED: { label: 'Report emailed', icon: <IosShareRoundedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(2,119,189,0.08)', fg: '#01579B', border: 'rgba(2,119,189,0.25)' },
    // Amendments are the events an auditor comes here looking for — a signed
    // clinical document changed after the fact. Given their own amber styling
    // so they stand out from routine views and exports.
    REPORT_AMEND_STARTED: { label: 'Amendment opened', icon: <EditOutlinedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(237,108,2,0.10)', fg: '#9A5800', border: 'rgba(237,108,2,0.25)' },
    REPORT_AMENDED: { label: 'Report amended', icon: <EditOutlinedIcon sx={{ fontSize: 13 }} />, bg: 'rgba(237,108,2,0.16)', fg: '#8A4B00', border: 'rgba(237,108,2,0.4)' },
};

function formatTimestamp(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

function patientLabel(p) {
    if (!p) return '';
    const name = `${p.fName || ''} ${p.lName || ''}`.trim() || 'Unnamed';
    return p.mrn ? `${name} · MRN ${p.mrn}` : name;
}

export default function AuditLog() {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const { isAdmin } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();

    const staffId = searchParams.get('staffId') || '';
    const action = searchParams.get('action') || '';
    const patientId = searchParams.get('patientId') || '';
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [staffOptions, setStaffOptions] = useState([]);

    // Patient autocomplete state — same debounced search the encounters
    // page uses, scoped to the current branch.
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);
    const [patientSearchInput, setPatientSearchInput] = useState('');
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const patientSearchSeq = useRef(0);

    const updateParams = useCallback((next) => {
        const merged = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(next)) {
            if (v === '' || v === null || v === undefined) merged.delete(k);
            else merged.set(k, v);
        }
        setSearchParams(merged, { replace: false });
    }, [searchParams, setSearchParams]);

    // Staff dropdown — loaded once.
    useEffect(() => {
        if (!isAdmin) return;
        apiGet('api/audit-logs/staff')
            .then((res) => setStaffOptions(res?.data || []))
            .catch(() => setStaffOptions([]));
    }, [isAdmin]);

    // Load log entries whenever filters or page change.
    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        setLoading(true);
        apiGet('api/audit-logs', {
            staffId, action, patientId, from, to,
            page, limit: PAGE_SIZE,
        })
            .then((res) => {
                if (cancelled) return;
                setItems(res?.data || []);
                setTotal(res?.total || 0);
                setPages(res?.pages || 1);
            })
            .catch(() => {
                if (cancelled) return;
                setItems([]);
                setTotal(0);
                setPages(1);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isAdmin, staffId, action, patientId, from, to, page]);

    const currentBranchId = (() => {
        try {
            const raw = localStorage.getItem('currentBranch');
            return raw ? (JSON.parse(raw)?.id || null) : null;
        } catch {
            return null;
        }
    })();

    // Debounced patient search for the filter.
    useEffect(() => {
        if (!isAdmin || !currentBranchId) return undefined;
        const handle = setTimeout(() => {
            const seq = ++patientSearchSeq.current;
            setPatientSearchLoading(true);
            apiGet(`api/get-patient-profile/${currentBranchId}`, {
                limit: 20,
                skip: 0,
                search: patientSearchInput || '',
                searchType: 'fullName',
            })
                .then((res) => {
                    if (seq !== patientSearchSeq.current) return;
                    setPatientOptions(res?.data || []);
                })
                .catch(() => {
                    if (seq !== patientSearchSeq.current) return;
                    setPatientOptions([]);
                })
                .finally(() => {
                    if (seq === patientSearchSeq.current) setPatientSearchLoading(false);
                });
        }, 250);
        return () => clearTimeout(handle);
    }, [isAdmin, patientSearchInput, currentBranchId]);

    const hasFilters = Boolean(staffId || action || patientId || from || to);
    const clearFilters = () => {
        setSelectedPatient(null);
        setPatientSearchInput('');
        setSearchParams(new URLSearchParams(), { replace: false });
    };

    if (!isAdmin) {
        return (
            <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
                    The audit log is only available to administrators.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: NAVY }}>
                    Audit Log
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Every action staff accounts take on patients and their records — views, changes, deletions and report activity.
                </Typography>
            </Box>

            {/* Filter bar */}
            <Paper
                variant="outlined"
                sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2, borderColor: 'rgba(5,47,93,0.10)', bgcolor: '#fff' }}
            >
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="audit-staff-label">Staff member</InputLabel>
                            <Select
                                labelId="audit-staff-label"
                                label="Staff member"
                                value={staffId}
                                onChange={(e) => updateParams({ staffId: e.target.value, page: '' })}
                            >
                                <MenuItem value="">All staff</MenuItem>
                                {staffOptions.map((s) => (
                                    <MenuItem key={s._id} value={s._id}>
                                        {s.name}{s.isDeleted ? ' (removed)' : ''} — {s.role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="audit-action-label">Action</InputLabel>
                            <Select
                                labelId="audit-action-label"
                                label="Action"
                                value={action}
                                onChange={(e) => updateParams({ action: e.target.value, page: '' })}
                            >
                                {ACTION_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={5}>
                        <Autocomplete
                            value={selectedPatient}
                            onChange={(_, val) => {
                                setSelectedPatient(val);
                                updateParams({ patientId: val?._id || '', page: '' });
                            }}
                            inputValue={patientSearchInput}
                            onInputChange={(_, val, reason) => {
                                if (reason !== 'reset') setPatientSearchInput(val);
                            }}
                            options={patientOptions}
                            getOptionLabel={patientLabel}
                            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                            loading={patientSearchLoading}
                            filterOptions={(opts) => opts}
                            noOptionsText={patientSearchInput ? 'No patients match' : 'Start typing a name or MRN'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Patient"
                                    placeholder="Search by name or MRN"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {patientSearchLoading ? <CircularProgress size={14} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={from}
                            onChange={(e) => updateParams({ from: e.target.value, page: '' })}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={to}
                            onChange={(e) => updateParams({ to: e.target.value, page: '' })}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            onClick={clearFilters}
                            disabled={!hasFilters}
                            startIcon={<FilterAltOffOutlinedIcon />}
                            sx={{ textTransform: 'none', color: NAVY, fontWeight: 600, whiteSpace: 'nowrap' }}
                            fullWidth
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Entries */}
            <Paper
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: { xs: 1, md: 1.5 }, bgcolor: '#fff' }}
            >
                {loading ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <CircularProgress size={26} sx={{ color: NAVY }} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box
                        sx={{
                            p: 5, textAlign: 'center',
                            border: '1px dashed rgba(5,47,93,0.15)', borderRadius: 2,
                            bgcolor: 'rgba(5,47,93,0.025)',
                        }}
                    >
                        <FactCheckOutlinedIcon sx={{ fontSize: 36, color: NAVY, opacity: 0.4 }} />
                        <Typography sx={{ mt: 1, color: NAVY, fontWeight: 600 }}>
                            No audit entries {hasFilters ? 'match these filters' : 'recorded yet'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {hasFilters
                                ? 'Try clearing a filter to widen the search.'
                                : 'Staff activity on patient records will appear here as it happens.'}
                        </Typography>
                    </Box>
                ) : (
                    <Stack divider={<Box sx={{ borderBottom: '1px solid rgba(5,47,93,0.06)' }} />}>
                        {items.map((l) => {
                            const display = EVENT_DISPLAY[l.eventType] || { label: l.eventType, bg: 'rgba(5,47,93,0.08)', fg: NAVY, border: 'rgba(5,47,93,0.15)' };
                            const actionChip = (
                                <Chip
                                    size="small"
                                    icon={display.icon}
                                    label={display.label + (l.success === false ? ' (failed)' : '')}
                                    sx={{
                                        bgcolor: l.success === false ? 'rgba(198,40,40,0.08)' : display.bg,
                                        color: l.success === false ? '#9B2C2C' : display.fg,
                                        border: '1px solid',
                                        borderColor: l.success === false ? 'rgba(198,40,40,0.25)' : display.border,
                                        fontWeight: 600,
                                        '.MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            );
                            return (
                                <Box
                                    key={l._id}
                                    sx={{
                                        p: { xs: 1.5, md: 1.75 },
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        alignItems: { xs: 'stretch', md: 'center' },
                                        gap: { xs: 0.75, md: 2 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: { md: 150 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                            {formatTimestamp(l.timestamp)}
                                        </Typography>
                                        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
                                            {actionChip}
                                        </Box>
                                    </Box>
                                    <Box sx={{ minWidth: { md: 190 }, maxWidth: { md: 190 } }}>
                                        <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 14 }} noWrap>
                                            {l.staffName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                            {l.staffRole}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
                                        {actionChip}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, color: 'text.primary' }} noWrap>
                                            {l.resourceType || 'Record'}
                                            {l.patientName && (
                                                <>
                                                    {' — '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>{l.patientName}</Box>
                                                    {l.patientMrn ? ` (MRN ${l.patientMrn})` : ''}
                                                </>
                                            )}
                                        </Typography>
                                        <Tooltip title={l.endpoint || ''} arrow>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                                                {l.action} {l.endpoint}
                                            </Typography>
                                        </Tooltip>
                                        {/* An amendment's stated reason is the
                                            substance of the entry — surface it
                                            on the row instead of burying it in
                                            metadata nobody can see. */}
                                        {l.metadata?.reason && (
                                            <Tooltip title={l.metadata.reason} arrow>
                                                <Typography variant="caption" sx={{ color: '#8A4B00', fontStyle: 'italic' }} noWrap component="div">
                                                    “{l.metadata.reason}”
                                                    {l.metadata.changedFields?.length
                                                        ? ` · changed: ${l.metadata.changedFields.join(', ')}`
                                                        : ''}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {total ? `${total.toLocaleString()} entries` : ''}
                </Typography>
                {pages > 1 && (
                    <Pagination
                        count={pages}
                        page={page}
                        onChange={(_, p) => updateParams({ page: p === 1 ? '' : String(p) })}
                        color="primary"
                        shape="rounded"
                        siblingCount={isXs ? 0 : 1}
                        size={isXs ? 'small' : 'medium'}
                    />
                )}
            </Box>
        </Container>
    );
}
